import { Case, Default, Switch, Unless } from 'react-if';
import { CalendarIcon } from '@zep-us/zus-icons';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { match, P } from 'ts-pattern';
import { useViewportDevice } from '@zep/react';
import { CountryCode, LabelStatus, PlanStatus } from '@zep/types';
import { Button } from '@zep/ui';
import { Gray600 } from '@zep/ui-tokens/color';
import { SpaceWithPlanDto, SubscribePlan } from '../../../../../../types/SubscriptionType';
import { fDateByCountryCode } from '../../../../../../utils';
import { ExportIcon } from '../../../../../icons';
import { ThumbnailCard } from '../../../../common/ThumbnailCard';
import { AdditionalLabel } from '../../atoms/AdditionalLabel';
import { useExpiredButtons } from '../../hooks/useExpiredButtons';
import { PlanLabelMaxCcu } from '../../molecules/PlanLabel/PlanLabelMaxCcu';
import { LinkToDashboard } from './LinkToDashboard';
import S from './PlanSpaceCard.module.scss';
export const PlanSpaceCard = (props: SubscribedSpaceCardProps) => {
  const {
    space
  } = props;
  const {
    planStatus,
    spaceHashId,
    spaceName,
    spaceUrl,
    customDomain,
    thumbnailUrl,
    maxCcu,
    planType,
    role: myRole,
    paymentFailCount,
    isWaitingForPayment
  } = space;
  const cleanedUrl = spaceUrl.replace('https://', '');
  const labelStatus = match({
    planStatus,
    paymentFailCount,
    isWaitingForPayment
  }).with({
    planStatus: PlanStatus.EXPIRED
  }, () => LabelStatus.EXPIRED).with({
    paymentFailCount: P.number.positive()
  }, () => LabelStatus.FAIL).with({
    planStatus: PlanStatus.READY,
    isWaitingForPayment: true
  }, () => LabelStatus.WAITING_FOR_PAYMENT).with({
    planStatus: PlanStatus.READY
  }, () => LabelStatus.READY).otherwise(() => LabelStatus.NONE);
  return <ThumbnailCard useThumbnailClick={false} thumbnailImg={thumbnailUrl} myRole={myRole} spaceHashId={spaceHashId} data-sentry-element="ThumbnailCard" data-sentry-component="PlanSpaceCard" data-sentry-source-file="PlanSpaceCard.tsx">
      <PlanLabelText plan={planType.toLowerCase() as SubscribePlan} maxCcu={maxCcu} labelStatus={labelStatus} data-sentry-element="PlanLabelText" data-sentry-source-file="PlanSpaceCard.tsx" />
      <div className={S.space_name}>{spaceName}</div>
      <SpaceLink url={spaceUrl} customDomain={customDomain} cleanedUrl={cleanedUrl} data-sentry-element="SpaceLink" data-sentry-source-file="PlanSpaceCard.tsx" />
      <Switch data-sentry-element="Switch" data-sentry-source-file="PlanSpaceCard.tsx">
        <Case condition={planStatus === PlanStatus.ACTIVE} data-sentry-element="Case" data-sentry-source-file="PlanSpaceCard.tsx">
          <PlanActive {...props} data-sentry-element="PlanActive" data-sentry-source-file="PlanSpaceCard.tsx" />
        </Case>
        <Case condition={planStatus === PlanStatus.EXPIRED} data-sentry-element="Case" data-sentry-source-file="PlanSpaceCard.tsx">
          <PlanExpired {...props} data-sentry-element="PlanExpired" data-sentry-source-file="PlanSpaceCard.tsx" />
        </Case>
        <Case condition={planStatus === PlanStatus.READY} data-sentry-element="Case" data-sentry-source-file="PlanSpaceCard.tsx">
          <PlanReady {...props} data-sentry-element="PlanReady" data-sentry-source-file="PlanSpaceCard.tsx" />
        </Case>
        <Default data-sentry-element="Default" data-sentry-source-file="PlanSpaceCard.tsx">
          <PlanExpired {...props} data-sentry-element="PlanExpired" data-sentry-source-file="PlanSpaceCard.tsx" />
        </Default>
      </Switch>
    </ThumbnailCard>;
};
type SubscribedSpaceCardProps = {
  space: SpaceWithPlanDto;
};
const PlanLabelText = ({
  plan,
  maxCcu,
  labelStatus
}: {
  plan: SubscribePlan;
  maxCcu: number;
  labelStatus: LabelStatus;
}) => {
  return <div className={S.space_plan_name} data-sentry-component="PlanLabelText" data-sentry-source-file="PlanSpaceCard.tsx">
      <PlanLabelMaxCcu plan={plan} maxCcu={maxCcu} data-sentry-element="PlanLabelMaxCcu" data-sentry-source-file="PlanSpaceCard.tsx">
        <Unless condition={labelStatus === LabelStatus.NONE} data-sentry-element="Unless" data-sentry-source-file="PlanSpaceCard.tsx">
          <AdditionalLabel status={labelStatus} data-sentry-element="AdditionalLabel" data-sentry-source-file="PlanSpaceCard.tsx" />
        </Unless>
      </PlanLabelMaxCcu>
    </div>;
};
const SpaceLink = ({
  url,
  customDomain,
  cleanedUrl
}: {
  url: string;
  customDomain?: string | null;
  cleanedUrl: string;
}) => {
  return <a className={S.gray_label_wrapper} style={{
    cursor: 'pointer'
  }} target={'_blank'} href={url} data-sentry-component="SpaceLink" data-sentry-source-file="PlanSpaceCard.tsx">
      <div>
        <ExportIcon width={16} height={16} fill={Gray600} data-sentry-element="ExportIcon" data-sentry-source-file="PlanSpaceCard.tsx" />
      </div>
      <label className={S.gray_label_text} style={{
      cursor: 'pointer'
    }}>
        {customDomain ? `zep.us@${customDomain}` : cleanedUrl}
      </label>
    </a>;
};
const PlanActive = ({
  space
}: {
  space: SpaceWithPlanDto;
}) => {
  const {
    t
  } = useTranslation(['subscription']);
  const router = useRouter();
  const {
    spaceHashId,
    countryCode,
    endDate,
    paymentExpectedDate
  } = space;
  const {
    isMobile
  } = useViewportDevice();
  const buttonSize = isMobile ? 'xs' : 'sm';
  const handleClickManage = () => {
    void router.push({
      pathname: '/me/spaces/[hashId]/plan',
      query: {
        hashId: spaceHashId
      }
    });
  };
  return <>
      <ShowDate countryCode={countryCode} date={paymentExpectedDate ?? endDate} dateTitle={space.paymentExpectedDate ? t('subscription:SpaceList.nextPaymentDate', '다음 결제일') : t('subscription:SpaceList.cardTitle.endDate', '만료일')} data-sentry-element="ShowDate" data-sentry-source-file="PlanSpaceCard.tsx" />
      <LinkToDashboard spaceHashId={spaceHashId} data-sentry-element="LinkToDashboard" data-sentry-source-file="PlanSpaceCard.tsx" />
      <div className={S.button_wrapper}>
        <Button size={buttonSize} variant={'sub'} isFullWidth onClick={handleClickManage} data-sentry-element="Button" data-sentry-source-file="PlanSpaceCard.tsx">
          {t('subscription:SpaceList.button.toManage')}
        </Button>
      </div>
    </>;
};
const ShowDate = ({
  date,
  dateTitle,
  countryCode
}: {
  date: string;
  dateTitle: string;
  countryCode: CountryCode;
}) => {
  return <div className={S.gray_label_wrapper} data-sentry-component="ShowDate" data-sentry-source-file="PlanSpaceCard.tsx">
      <div>
        <CalendarIcon width={16} height={16} fill={Gray600} data-sentry-element="CalendarIcon" data-sentry-source-file="PlanSpaceCard.tsx" />
      </div>
      <label className={S.gray_label_text}>
        {`${dateTitle} : ${fDateByCountryCode(date, countryCode)}`}
      </label>
    </div>;
};
const PlanExpired = ({
  space
}: {
  space: SpaceWithPlanDto;
}) => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    handleClickUpgradePlan,
    handleClickManagePlan
  } = useExpiredButtons(space);
  const {
    isMobile
  } = useViewportDevice();
  const buttonSize = isMobile ? 'xs' : 'sm';
  return <>
      <LinkToDashboard spaceHashId={space.spaceHashId} data-sentry-element="LinkToDashboard" data-sentry-source-file="PlanSpaceCard.tsx" />
      <div className={S.button_wrapper}>
        <Button variant={'secondary'} size={buttonSize} isFullWidth onClick={handleClickUpgradePlan} data-sentry-element="Button" data-sentry-source-file="PlanSpaceCard.tsx">
          {t('subscription:pricing.plansCommon.buttonUpgrade')}
        </Button>
        <Button variant={'sub'} size={buttonSize} isFullWidth onClick={handleClickManagePlan} data-sentry-element="Button" data-sentry-source-file="PlanSpaceCard.tsx">
          {t('subscription:SpaceList.button.toManage')}
        </Button>
      </div>
    </>;
};
const PlanReady = ({
  space
}: {
  space: SpaceWithPlanDto;
}) => {
  const {
    t
  } = useTranslation(['subscription']);
  const router = useRouter();
  const {
    spaceHashId,
    countryCode,
    startDate
  } = space;
  const {
    isMobile
  } = useViewportDevice();
  const buttonSize = isMobile ? 'xs' : 'sm';
  const handleClickManage = () => {
    void router.push({
      pathname: '/me/spaces/[hashId]/plan',
      query: {
        hashId: spaceHashId
      }
    });
  };
  return <>
      <ShowDate countryCode={countryCode} date={startDate} dateTitle={t('subscription:SpaceList.cardTitle.startDate')} data-sentry-element="ShowDate" data-sentry-source-file="PlanSpaceCard.tsx" />
      <LinkToDashboard spaceHashId={spaceHashId} data-sentry-element="LinkToDashboard" data-sentry-source-file="PlanSpaceCard.tsx" />
      <div className={S.button_wrapper}>
        <Button size={buttonSize} variant={'sub'} isFullWidth onClick={handleClickManage} data-sentry-element="Button" data-sentry-source-file="PlanSpaceCard.tsx">
          {t('subscription:SpaceList.button.toManage')}
        </Button>
      </div>
    </>;
};